import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { media } from "../../data/media";
import { useVimeoMeta } from "../../hooks/useVimeoMeta";
import { aspectBadges, aspectFrames, kindFromAspect } from "../../utils/work";
import { vimeoLoopUrl } from "../../utils/vimeo";
import type { WorkItem } from "../../types/work";
type WorkCardProps = {
  item: WorkItem;
  scrolling: boolean;
  onOpen: (item: WorkItem) => void;
  /** Let the media panel follow the film's real shape instead of a fixed 16:9 tile. */
  fitMedia?: boolean;
  /** Dense grids preview on hover; the collage previews whenever scrolling stops. */
  loopOn?: "idle" | "hover";
};
export function WorkCard({
  item,
  scrolling,
  onOpen,
  fitMedia = false,
  loopOn = "idle"
}: WorkCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [started, setStarted] = useState(false);
  const reduceMotion = useReducedMotion();
  const meta = useVimeoMeta(item.vimeoId);
  const aspect = item.aspect ?? meta.aspect ?? "landscape";
  const title = item.title ?? meta.title ?? "Untitled film";
  const kind = item.kind ?? kindFromAspect(aspect);
  const cover = meta.thumbnail ?? item.posterUrl ?? (item.plate ? (media as Record<string, string>)[item.plate] : undefined) ?? media.archivePlate;
  const playable = Boolean(item.vimeoId || item.videoUrl);
  // A vertical film inside a fixed 16:9 tile is letterboxed rather than cropped to a slice.
  const letterboxed = !fitMedia && aspect !== "landscape";
  // Once a loop has started it stays mounted for as long as the card is in view.
  // Unmounting it on every scroll would send the film back to its first frame.
  const idleLoop = loopOn === "idle" ? inView && (started || !scrolling) : hovered;
  const loopFrame = Boolean(item.vimeoId) && idleLoop && !reduceMotion;
  useEffect(() => {
    if (loopOn !== "idle") return;
    if (inView && !scrolling) setStarted(true);
    if (!inView) setStarted(false);
  }, [inView, scrolling, loopOn]);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: "200px 0px"
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !item.videoUrl) return;
    if (inView && !scrolling && !reduceMotion) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [inView, scrolling, item.videoUrl, reduceMotion]);
  return <div ref={containerRef} className="scroll-blur" data-id="element-520" data-mp="wgy5hxcxi4rv">
      <motion.button type="button" onClick={() => onOpen(item)} onPointerEnter={() => setHovered(true)} onPointerLeave={() => setHovered(false)} onFocus={() => setHovered(true)} onBlur={() => setHovered(false)} data-cursor={playable ? "Watch" : "View"} aria-label={playable ? `Watch ${title}` : `View ${title}`} whileHover={reduceMotion ? undefined : {
      y: -8
    }} transition={{
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }} className="group block w-full rounded-[18px] bg-[#eeeeeb] p-3 text-left shadow-[0_30px_70px_-30px_rgba(0,0,0,0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050808] md:p-4" data-id="element-521" data-mp="wjke80bk2qbo">
        <div className={`relative overflow-hidden rounded-[10px] bg-[#0c100f] ${fitMedia ? aspectFrames[aspect] : "aspect-video"}`} data-id="element-522" data-mp="jzwu2ha2d3kw">
          {/* Soft backdrop fill, so letterboxed reels never sit against dead black. */}
          {letterboxed && <img src={cover} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-40 blur-2xl" loading="lazy" data-id="element-523" data-mp="3ip435p6u0uuc" />}

          {loopFrame && item.vimeoId ? <iframe src={vimeoLoopUrl(item.vimeoId)} title="" aria-hidden="true" tabIndex={-1} allow="autoplay; picture-in-picture" className={letterboxed ? "pointer-events-none absolute left-1/2 top-1/2 h-full w-auto -translate-x-1/2 -translate-y-1/2 border-0 [aspect-ratio:9/16]" : "pointer-events-none absolute inset-0 h-full w-full border-0"} data-id="element-524" data-mp="2qxd8aohuouhz" /> : item.videoUrl ? <video ref={videoRef} className="relative h-full w-full object-cover" src={item.videoUrl} poster={cover} muted loop playsInline preload="none" tabIndex={-1} aria-hidden="true" data-id="element-525" data-mp="3ejs07mltefsw" /> : <img src={cover} alt="" aria-hidden="true" className={`relative h-full w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] ${letterboxed ? "object-contain" : "object-cover"}`} loading="lazy" data-id="element-526" data-mp="1gqzc58s169f2" />}

          <span className="absolute left-3 top-3 rounded-full bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/85 backdrop-blur-sm" data-id="element-527" data-mp="1zpbkwtf15ha4">
            {kind}{item.index ? ` ${item.index}` : ""}
          </span>
          <span className="absolute right-3 top-3 rounded-full bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/75 backdrop-blur-sm" data-id="element-528" data-mp="1qmaib2pdi8b7">
            {aspectBadges[aspect]}
          </span>
          <span aria-hidden="true" className="absolute bottom-3 right-3 flex h-9 items-center gap-2 rounded-full bg-[#050808]/75 px-3.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100" data-id="element-529" data-mp="f6lw4djy1dbg">
            {playable ? <Play className="h-3 w-3 fill-current" data-id="element-530" data-mp="gnrkij5l8m6s" /> : <ArrowUpRight className="h-3 w-3" data-id="element-531" data-mp="2r49jcgsvvhv" />}
            {playable ? "Play" : "Open"}
          </span>
        </div>

        <h3 className={`mt-6 px-1 font-semibold leading-[0.95] tracking-[-0.045em] text-[#0a0e0d] ${fitMedia ? "text-[clamp(1.15rem,1.5vw,1.6rem)] leading-[1.05]" : "text-[clamp(1.35rem,2.1vw,2.1rem)]"}`} data-id="element-532" data-mp="6qmug88mg6kp">
          {title}
        </h3>

        {item.tags && item.tags.length > 0 && <div className={`flex flex-wrap gap-x-7 gap-y-4 px-1 pb-2 ${fitMedia ? "mt-5" : "mt-8"}`} data-id="element-533" data-mp="3f5oihl4nk0fp">
            {item.tags.map(tag => <span key={tag} className="block" data-id="element-534" data-mp="366t9k1a84c8p">
                <span aria-hidden="true" className="mb-2 block h-3 w-px bg-[#0a0e0d]/45" data-id="element-535" data-mp="36961n79p42zh" />
                <span className="block font-mono text-[11px] uppercase tracking-[0.12em] text-[#0a0e0d]/75" data-id="element-536" data-mp="3b5cwo50jzu2n">{tag}</span>
              </span>)}
          </div>}
      </motion.button>
    </div>;
}